<template>
    <v-dialog v-model="addEditFolderDialogHandle" transition="dialog-bottom-transition" max-width="600px"
        @keydown.esc="closeAddEditFolderDialog()" @click:outside="closeAddEditFolderDialog()">
        <v-card>
            <v-form @submit.prevent.native="addEditAFolder()">
                <v-toolbar color="primary" dark :class="dialogHeadingClasses()">
                    <v-icon v-text="isAdd ? icons.mdiPlusCircleOutline : icons.mdiPencilCircleOutline"
                        class="mr-3"></v-icon>
                    <div v-text="$gettext('Add Folder')" v-if="isAdd"></div>
                    <div v-text="$gettext('Edit Folder')" v-else-if="isEdit"></div>
                    <v-spacer></v-spacer>
                    <v-icon class="mr-2" @click.native.stop="closeAddEditFolderDialog()"
                        v-text="icons.mdiClose"></v-icon>
                    <v-icon @click.native.stop="addEditAFolder()" v-text="icons.mdiContentSave"></v-icon>
                </v-toolbar>

                <v-divider></v-divider>

                <v-card-text class="mt-8">
                    <v-container>
                        <v-row>
                            <v-col cols="12">
                                <v-text-field :label="$gettext('Folder Name')" :placeholder="$gettext('Folder Name')"
                                    required v-model="addEditFolderName" :error-messages="addEditFolderNameErrors"
                                    outlined @input="$v.addEditFolderName.$touch()"
                                    @blur="$v.addEditFolderName.$touch()" autofocus clearable
                                    :prepend-inner-icon="icons.mdiFolderOutline"></v-text-field>
                                <v-checkbox v-model="nextAction" v-if="$props.mode === 'add'"
                                    :label="getNextActionLabel()">
                                </v-checkbox>
                            </v-col>
                        </v-row>
                    </v-container>
                </v-card-text>

                <v-divider></v-divider>

                <v-card-actions color="secondary">
                    <v-spacer></v-spacer>
                    <v-btn color="primary" @click.native.stop="closeAddEditFolderDialog()"
                        v-text="$gettext('Close')"></v-btn>
                    <v-btn color="primary" type="submit" v-text="$gettext('Save')"></v-btn>
                </v-card-actions>
            </v-form>
        </v-card>
    </v-dialog>
</template>
<script>

import { required } from 'vuelidate/lib/validators'

import MixinDialog from '@/mixins/MixinDialog'
import MixinIcon from '@/mixins/MixinIcon'
import Snackbar from '@/components/Snackbar'
import MixinStringManipulation from '@/mixins/MixinStringManipulation'
import MixinVuelidate from '@/mixins/MixinVuelidate'

export default {
    components: { Snackbar },
    mixins: [MixinDialog, MixinIcon, MixinStringManipulation, MixinVuelidate],
    props: {
        groupsProp: {
            required: true,
            type: Array | Object
        },
        addEditFolderDialog: {
            required: true,
            type: Boolean,
            default: false
        },

        mode: {
            type: String, // Expected: add/edit
            required: true,
            default: '',
        },

        inEditFolderName: {
            required: false,
            default: ''
        },

    },
    data() {
        return {
            addEditFolderName: '',
            addEditFolderDialogHandle: '',
            nextAction: true,
        }
    },
    watch: {
        /**
         * When a popup is closed, the previous entry is left as is.
         * Also, when a popup is opened with other entry the value is not refreshed, with the new one, if we don't have this.
         * @param newVal
         */
        addEditFolderDialog(newVal) {

            let self = this

            if (!newVal) {
                self.resetForm()
            } else {
                self.addEditFolderName = self.$props.inEditFolderName
            }

        }
    },

    mounted() {

        let self = this

        // Mounted only fills in the dialog for the first time, the rest of the time it is filled by the watcher
        self.addEditFolderName = self.$props.inEditFolderName
        self.addEditFolderDialogHandle = self.$props.addEditFolderDialog

    },

    methods: {

        getNextActionLabel() {

            let self = this
            let str = '';

            if (self.addEditFolderName) {
                str = self.$gettext('Next, add a group for %{ folderName }')

                str = this.$gettextInterpolate(str, {
                    folderName: self.addEditFolderName
                })
            } else {
                str = self.$gettext('Next, add a group for this folder')
            }

            return str
        },

        addEditAFolder() {

            let self = this
            self.$v.$touch()

            if (!self.$v.$invalid) {

                if (self.isAdd) {
                    self.$emit('folder-added', {
                        key: self.addEditFolderName,
                        child: [],
                        nextAction: self.nextAction
                    })
                } else {
                    self.$emit('folder-updated', self.addEditFolderName)
                }

                self.closeAddEditFolderDialog()
                self.resetForm()

            }

        },

        closeAddEditFolderDialog() {
            let self = this
            self.$emit('close-add-edit-folder-dialog', 'addEditFolderDialog')
        },
        resetForm() {
            let self = this
            self.addEditFolderName = ''
            self.$v.$reset()
        },
    },
    computed: {

        isAdd() {
            return this.$props.mode === 'add'
        },
        isEdit() {
            return this.$props.mode === 'edit'
        },
        addEditFolderNameErrors() {
            const errors = []
            if (!this.$v.addEditFolderName.$dirty) return errors
            !this.$v.addEditFolderName.required && errors.push(this.$gettext('Folder name is required.'))
            !this.$v.addEditFolderName.isUnique && errors.push(this.$gettext('We already have this folder name with us.'))
            !this.$v.addEditFolderName.doesNotContainSlash && errors.push(this.$gettext('"/" is not an allowed character for the folder name'))
            return errors
        }

    },
    validations: {
        addEditFolderName: {
            required,
            async doesNotContainSlash(value, vm) {
                return !value.includes('/');
            },
            async isUnique(value, vm) {
                let allowedValue = vm.mode === 'edit' ? vm.$props.inEditFolderName : null
                return await this.vuelidateIsUniqueForKeyValueType(value, this.$props.groupsProp, allowedValue)
            }
        }
    }

}
</script>

<style type="text/scss">
.add-group-radio-margin .v-input--selection-controls {
    margin-top: 0;
}
</style>
